import { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useTranslation } from "react-i18next";

import FullCalendar from "@fullcalendar/react";
import type { EventClickArg, DatesSetArg, EventContentArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import enLocale from "@fullcalendar/core/locales/en-gb";

import {
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Typography,
  Popover,
  CircularProgress,
} from "@mui/material";
import { ChevronLeft, ChevronRight, DateRange } from "@mui/icons-material";

import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useAuth } from "../../context/AuthContext";
import { useReservas } from "../../hooks/useReservas";
import ReservaDialog from "../../components/ReservaDialog";
import { COLOR_ESTADO } from "../../constants/colors";

/* ────────── helper título de cabecera ────────── */
function headerTxt(view: string, start: dayjs.Dayjs, end: dayjs.Dayjs, locale: string) {
  start = start.locale(locale);
  end = end.locale(locale);

  if (view === "dayGridMonth") return start.add(15, "day").format("MMMM YYYY");
  if (view === "timeGridWeek") return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
  return start.format("dddd D MMMM YYYY");
}

/* ────────── helper contenido evento ────────── */
function renderEventContent(arg: EventContentArg) {
  const { event } = arg;
  const { nombreAsignatura } = event.extendedProps as any;

  const ini = dayjs(event.start!).format("HH:mm");
  const fin = dayjs(event.end!).format("HH:mm");
  return { html: `${ini}-${fin} ${nombreAsignatura}` };
}

/* ═════════════════ COMPONENTE ═════════════════ */
export default function ClienteAgendaPage() {
  const { t, i18n } = useTranslation<"translation">();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [pendingId, setPendingId] = useState<number | null>(null);

  useEffect(() => {
    if (location.state?.reservaId) {
      setPendingId(location.state.reservaId as number);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.reservaId, navigate, location.pathname]);

  const [range, setRange] = useState<{
    start: dayjs.Dayjs | null;
    end: dayjs.Dayjs | null;
  }>({ start: null, end: null });

  const [view, setView] = useState<"dayGridMonth" | "timeGridWeek" | "timeGridDay">("dayGridMonth");

  const { rows, loading } = useReservas(
    range.start ? range.start.format("YYYY-MM-DD") : null,
    range.end ? range.end.format("YYYY-MM-DD") : null,
    user ? { clienteIdUsr: user.id } : {}
  );

  const [selected, setSelected] = useState<any | null>(null);
  useEffect(() => {
    if (!pendingId) return;
    const r = rows.find((x) => x.id === pendingId);
    if (r) {
      setSelected(r);
      setPendingId(null);
    }
  }, [pendingId, rows]);

  const events = useMemo(
    () =>
      rows.map((r) => ({
        id: String(r.id),
        start: r.inicio,
        end: r.fin,
        title: " ",
        backgroundColor: COLOR_ESTADO[r.estado as keyof typeof COLOR_ESTADO] ?? "#9e9e9e",
        extendedProps: { nombreAsignatura: r.nombreAsignatura },
      })),
    [rows]
  );

  const calRef = useRef<FullCalendar>(null);
  const nav = (fn: "next" | "prev") => calRef.current?.getApi()[fn]();
  const change = (v: string | null) => v && calRef.current?.getApi().changeView(v);

  const handleDatesSet = (arg: DatesSetArg) => {
    const newStart = dayjs(arg.start);
    const newEnd =
      arg.view.type === "dayGridMonth" ? dayjs(arg.end).subtract(1, "day") : dayjs(arg.end);

    const isSameRange = range.start?.isSame(newStart, "day") && range.end?.isSame(newEnd, "day");

    if (isSameRange) return;

    setRange({ start: newStart, end: newEnd });
    setView(arg.view.type as any);
  };

  const handleEventClick = (arg: EventClickArg) =>
    setSelected(rows.find((r) => r.id === Number(arg.event.id)));

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const goto = (d: dayjs.Dayjs | null) => {
    if (d) calRef.current?.getApi().gotoDate(d.toDate());
    setAnchor(null);
  };

  const header =
    range.start && range.end ? headerTxt(view, range.start, range.end, i18n.language) : "";

  if (!user) return <CircularProgress sx={{ m: 4 }} />;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => nav("prev")}>
          <ChevronLeft />
        </IconButton>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <DateRange />
        </IconButton>
        <IconButton size="small" onClick={() => nav("next")}>
          <ChevronRight />
        </IconButton>

        <Typography variant="h6" sx={{ ml: 2, flexGrow: 1, textTransform: "capitalize" }}>
          {header}
        </Typography>

        <ToggleButtonGroup size="small" value={view} exclusive onChange={(_, v) => change(v)}>
          <ToggleButton value="dayGridMonth">{t("clienteAgenda.vista.mes")}</ToggleButton>
          <ToggleButton value="timeGridWeek">{t("clienteAgenda.vista.semana")}</ToggleButton>
          <ToggleButton value="timeGridDay">{t("clienteAgenda.vista.dia")}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
          <DateCalendar
            value={null}
            onChange={goto}
            showDaysOutsideCurrentMonth
            fixedWeekNumber={6}
          />
        </LocalizationProvider>
      </Popover>

      {loading && <CircularProgress size={28} sx={{ position: "absolute", top: 8, right: 8 }} />}

      <FullCalendar
        ref={calRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        height="auto"
        locales={[esLocale, enLocale]}
        locale={i18n.language}
        firstDay={1}
        headerToolbar={false}
        initialView="dayGridMonth"
        eventDisplay="block"
        selectable={false}
        events={events}
        eventContent={renderEventContent}
        eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
      />

      <ReservaDialog
        open={!!selected}
        reserva={selected ?? undefined}
        onClose={() => setSelected(null)}
        onEdit={() => {}}
      />
    </>
  );
}
